import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Reader from "@/pages/Reader";
import Search from "@/pages/Search";
import Bookmarks from "@/pages/Bookmarks";
import Community from "@/pages/Community";
import Plans from "@/pages/Plans";
import Profile from "@/pages/Profile";
import Prayers from "@/pages/Prayers";
import Studies from "@/pages/Studies";
import BookReader from "@/pages/BookReader";
import Support from "@/pages/Support";
import Devotional from "@/pages/Devotional";
import EarlyAccess from "@/pages/EarlyAccess";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import PrivacyGate from "@/components/PrivacyGate";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, authLoading } = useApp();
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse"></span>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return (
    <PrivacyGate>
      <Layout>{children}</Layout>
    </PrivacyGate>
  );
}

function LandingOrRedirect() {
  const { user, authLoading } = useApp();
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse"></span>
      </div>
    );
  }
  if (user) return <Navigate to="/home" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=") || location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrRedirect />} />
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/read" element={<ProtectedRoute><Reader /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/prayers" element={<ProtectedRoute><Prayers /></ProtectedRoute>} />
      <Route path="/plans" element={<ProtectedRoute><Plans /></ProtectedRoute>} />
      <Route path="/studies" element={<ProtectedRoute><Studies /></ProtectedRoute>} />
      <Route path="/studies/book/:bookId" element={<ProtectedRoute><BookReader /></ProtectedRoute>} />
      <Route path="/devocional" element={<ProtectedRoute><Devotional /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/early-access" element={<ProtectedRoute><EarlyAccess /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/privacy" element={<Privacy />} />
    </Routes>
  );
}

function App() {
  // Restore dark mode preference on boot
  React.useEffect(() => {
    if (localStorage.getItem("lumen_dark") === "1") {
      document.documentElement.classList.add("dark-mode");
    }
  }, []);

  return (
    <AppProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "var(--surface-elevated)",
              color: "var(--text-primary)",
              border: "1px solid var(--border)",
            },
          }}
        />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
